// src/components/surveys/builder/QuestionTypeSelector.tsx
import React, { useState } from 'react';
import {
  Plus,
  CheckCircle,
  List,
  CheckSquare,
  Type,
  Hash,
  Star,
  ChevronDown,
  Calendar,
  Phone,
  Mail,
  X,
} from 'lucide-react';
import { QuestionType } from '../../../types/survey.types';

interface QuestionTypeSelectorProps {
  onSelect: (type: QuestionType) => void;
  position?: 'inline' | 'dropdown';
}

interface QuestionTypeInfo {
  type: QuestionType;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const QUESTION_TYPES: QuestionTypeInfo[] = [
  {
    type: 'yes_no',
    label: 'Yes/No',
    description: 'Simple yes or no answer',
    icon: <CheckCircle className="h-5 w-5" />,
    color: 'bg-green-100 text-green-600',
  },
  {
    type: 'multiple_choice',
    label: 'Multiple Choice',
    description: 'Select one option from a list',
    icon: <List className="h-5 w-5" />,
    color: 'bg-blue-100 text-blue-600',
  },
  {
    type: 'multi_select',
    label: 'Multi-Select',
    description: 'Select multiple options',
    icon: <CheckSquare className="h-5 w-5" />,
    color: 'bg-purple-100 text-purple-600',
  },
  {
    type: 'dropdown',
    label: 'Dropdown',
    description: 'Select from a dropdown menu',
    icon: <ChevronDown className="h-5 w-5" />,
    color: 'bg-cyan-100 text-cyan-600',
  },
  {
    type: 'text',
    label: 'Text',
    description: 'Free text response',
    icon: <Type className="h-5 w-5" />,
    color: 'bg-gray-100 text-gray-600',
  },
  {
    type: 'number',
    label: 'Number',
    description: 'Numeric input',
    icon: <Hash className="h-5 w-5" />,
    color: 'bg-orange-100 text-orange-600',
  },
  {
    type: 'rating_scale',
    label: 'Rating Scale',
    description: 'Rate on a scale (1-5, 1-10, etc.)',
    icon: <Star className="h-5 w-5" />,
    color: 'bg-yellow-100 text-yellow-600',
  },
  {
    type: 'date',
    label: 'Date',
    description: 'Date picker',
    icon: <Calendar className="h-5 w-5" />,
    color: 'bg-pink-100 text-pink-600',
  },
  {
    type: 'phone',
    label: 'Phone',
    description: 'Phone number input',
    icon: <Phone className="h-5 w-5" />,
    color: 'bg-indigo-100 text-indigo-600',
  },
  {
    type: 'email',
    label: 'Email',
    description: 'Email address input',
    icon: <Mail className="h-5 w-5" />,
    color: 'bg-teal-100 text-teal-600',
  },
];

const QuestionTypeSelector: React.FC<QuestionTypeSelectorProps> = ({
  onSelect,
  position = 'dropdown',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (type: QuestionType) => {
    onSelect(type);
    setIsOpen(false);
  };

  if (position === 'inline') {
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {QUESTION_TYPES.map((qt) => (
          <button
            key={qt.type}
            onClick={() => handleSelect(qt.type)}
            className="flex flex-col items-center p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-all group"
          >
            <div className={`p-3 rounded-lg mb-2 ${qt.color}`}>{qt.icon}</div>
            <span className="text-sm font-medium text-gray-900 group-hover:text-indigo-700">
              {qt.label}
            </span>
            <span className="text-xs text-gray-500 text-center mt-1">
              {qt.description}
            </span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
      >
        <Plus className="h-5 w-5" />
        Add Question
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute left-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="font-medium text-gray-900">Add Question</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-2 max-h-[400px] overflow-y-auto">
              {QUESTION_TYPES.map((qt) => (
                <button
                  key={qt.type}
                  onClick={() => handleSelect(qt.type)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <div className={`p-2 rounded-lg ${qt.color}`}>{qt.icon}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">{qt.label}</p>
                    <p className="text-sm text-gray-500 truncate">{qt.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default QuestionTypeSelector;
